import { shallowRef, triggerRef } from 'vue';

const CONCURRENCY = 3;
const MAX_PER_SOURCE = 10;

const readyUrls = shallowRef<Set<string>>(new Set());

interface QueueEntry {
  url: string;
}

let queue: QueueEntry[] = [];
let inflight = 0;
const pendingImages = new Map<string, HTMLImageElement>();
let generationId = 0;

function markReady(url: string) {
  readyUrls.value.add(url);
  triggerRef(readyUrls);
}

function processNext(gen: number) {
  if (gen !== generationId) return;

  while (inflight < CONCURRENCY && queue.length > 0) {
    const entry = queue.shift()!;
    if (readyUrls.value.has(entry.url)) continue;

    inflight++;
    const img = new Image();
    pendingImages.set(entry.url, img);

    img.onload = () => {
      pendingImages.delete(entry.url);
      inflight--;
      markReady(entry.url);
      processNext(gen);
    };

    img.onerror = () => {
      pendingImages.delete(entry.url);
      inflight--;
      processNext(gen);
    };

    img.src = entry.url;
  }
}

const sources = new Map<string, string[]>();

function rebuildQueue() {
  for (const img of pendingImages.values()) {
    img.src = '';
    img.onload = null;
    img.onerror = null;
  }
  pendingImages.clear();
  inflight = 0;

  generationId++;
  const gen = generationId;

  const iterators = [...sources.values()].map((urls) => ({
    urls: urls.filter((u) => !readyUrls.value.has(u)).slice(0, MAX_PER_SOURCE),
    idx: 0,
  }));

  queue = [];
  let added = true;
  while (added) {
    added = false;
    for (const it of iterators) {
      if (it.idx < it.urls.length) {
        queue.push({ url: it.urls[it.idx] });
        it.idx++;
        added = true;
      }
    }
  }

  processNext(gen);
}

export function schedule(urls: string[], label = 'unknown') {
  sources.set(label, urls);
  rebuildQueue();
}

export function unschedule(label: string) {
  sources.delete(label);
}

export function getReadyUrls() {
  return readyUrls;
}
